/**
 * Shared credit (docs/shares.md, flag `shareCreditUI`; migration 0060). Owner, 2026-11-02: "Should be
 * able to share task credits on things", then picking "Split plus a teamwork bonus" for the gold: a job
 * shared between peons pays each of them an even split of its gold plus a quarter more, and each counts
 * the job. A project's progress posters share it the same way (docs/projects.md).
 *
 * The words both clients and the server use, the split, and the name rules. Who may share is
 * shared/permissions.ts `canGiveCredit` (the holder or a steward, as for Give credit); what moves is
 * server/shares.ts. Pure, so the Node tests import it by extension.
 */
import { normalizeName } from "./peons.ts";
import type { Task } from "./types";

/** The teamwork bonus: each peon on a shared job gets an even split of its gold and this much more. */
export const TEAM_BONUS = 0.25;

/**
 * Each credited peon's gold when a job that paid `pot` is shared between `n` peons: the whole pot for one
 * alone, else the even split with the teamwork bonus, rounded up (owner, 2026-11-02, "Split plus a
 * teamwork bonus"). A 10 gold job shared by two pays 7 each; a 100 gold project shared by three, 42 each.
 */
export function shareGold(pot: number, n: number): number {
  const whole = Math.max(0, Math.round(Number.isFinite(pot) ? pot : 0));
  if (n <= 1 || whole === 0) return whole;
  return Math.ceil((whole * (1 + TEAM_BONUS)) / n);
}

/** The written words. Plain, as the app speaks; nothing here is said aloud. */
export const SHARE_WORDS = {
  /** The quiet word at the done task's foot, beside Give credit. */
  button: "Share credit",
  /** The names row's label, on the done task and on Job's Done's photo check. */
  heading: "Who helped?",
  /** The app's alert answers (the web's browser dialog keeps its own OK and Cancel). */
  ok: "Share it",
  cancel: "Keep it",
  /** The app's alert answer when a name comes off. */
  remove: "Take off",
  /** After a shared job's gold on the done task: "7 gold each". */
  each: "each",
} as const;

/** The confirm when a name joins: "Share credit with Hue? The gold is split, with a quarter more each." */
export function shareQuestion(name: string): string {
  return `Share credit with ${name}? The gold is split, with a quarter more each.`;
}

/** The confirm when a name comes off: "Take Hue off this job? The gold is split again." */
export function unshareQuestion(name: string): string {
  return `Take ${name} off this job? The gold is split again.`;
}

/** The 409 for a task that is not done (reopened meanwhile, or never finished). */
export const SHARE_NOT_DONE = "This task isn't done.";

/** The 409 when the share list changed under the request (another share, a credit or a reopen). */
export const SHARE_MOVED = "Someone changed this job. Try again.";

/** The 400 when a name is none of the household's. */
export const SHARE_UNKNOWN_NAME = "Nobody here by that name.";

/** The 403 when the caller neither holds the credit nor is a steward: "Only Hue or a steward can share this credit." */
export function shareRefusal(holder: string | null | undefined): string {
  return holder ? `Only ${holder} or a steward can share this credit.` : "Only a steward can share this credit.";
}

/** How many names may share one job besides the peon who finished it. */
export const SHARE_MAX = 11;

const key = (name: string | null | undefined) => normalizeName(name ?? "").toLowerCase();

/** True when `a` and `b` name the same peon: trimmed, spaces collapsed, any case. */
export function sameShareName(a: string | null | undefined, b: string | null | undefined): boolean {
  const k = key(a);
  return k !== "" && k === key(b);
}

/** Everyone credited with a done task: the finisher first, then the peons it is shared with. [] while open. */
export function creditedNames(task: Pick<Task, "status" | "done_by" | "shared_with">): string[] {
  if (task.status !== "done" || !task.done_by) return [];
  return [task.done_by, ...(task.shared_with ?? []).filter((n) => !sameShareName(n, task.done_by))];
}

/** True when `name` is credited with the done task, as its finisher or a sharer. */
export function isCredited(task: Pick<Task, "status" | "done_by" | "shared_with">, name: string | null | undefined): boolean {
  return creditedNames(task).some((n) => sameShareName(n, name));
}

/**
 * The names a list of helpers keeps: each once (the first spelling wins), never `doneBy`, never blank,
 * at most SHARE_MAX. The server then maps each to its registered spelling and refuses a stranger.
 */
export function cleanShareList(names: ReadonlyArray<string>, doneBy: string | null | undefined): string[] {
  const out: string[] = [];
  for (const raw of names) {
    const n = normalizeName(raw);
    if (!n || sameShareName(n, doneBy) || out.some((o) => sameShareName(o, n))) continue;
    out.push(n);
  }
  return out.slice(0, SHARE_MAX);
}

/** The list with `name` added, or taken off when it is there already. */
export function toggleShare(list: ReadonlyArray<string>, name: string): string[] {
  return list.some((n) => sameShareName(n, name)) ? list.filter((n) => !sameShareName(n, name)) : [...list, name];
}

/** "Hue", "Hue and Jeff", "Hue, Jeff and Bones": the credited names as the meta line reads them. */
export function namesLine(names: ReadonlyArray<string>): string {
  if (names.length <= 1) return names[0] ?? "";
  return `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
}

/** Each peon's part of a done task's gold for the meta line: the whole for one, the even part for a shared job. */
export function goldEach(task: Pick<Task, "status" | "done_by" | "shared_with" | "gold_awarded">): number {
  const n = Math.max(1, creditedNames(task).length);
  return Math.round((task.gold_awarded ?? 0) / n);
}

/** The purse's muted note on a share's line: "shared with Hue", "shared with Hue and Jeff". */
export function sharedNote(others: ReadonlyArray<string>): string {
  return others.length ? `shared with ${namesLine(others)}` : "shared";
}

/**
 * Job's Done's "Who helped?" pills (shareCreditUI): every household name but the finisher's, in the
 * household's order. `locked` names (a project's progress posters, docs/projects.md) are in already and
 * cannot be taken off: the server adds them whatever is sent.
 */
export function helperChoices(everyone: ReadonlyArray<string>, me: string, locked: ReadonlyArray<string> = []): { name: string; locked: boolean }[] {
  return everyone.filter((n) => !sameShareName(n, me)).map((name) => ({ name, locked: locked.some((l) => sameShareName(l, name)) }));
}

/** The reopen confirm's second sentence: "Hue gives back the gold.", "Hue and Jeff give back the gold." */
export function giveBackLine(names: ReadonlyArray<string>): string {
  return `${namesLine(names)} ${names.length > 1 ? "give" : "gives"} back the gold.`;
}
